import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Row, Col, Select, Switch } from 'antd';
import { SlackOutlined, UsergroupAddOutlined, SettingOutlined, BulbOutlined, MoonOutlined, } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import axios from '../../config/axiosInstance'; 

const { Option } = Select;

const ProfilePage = () => {
  const { user, setUser } = useAuth();
  const { setDarkTheme } = useTheme();
  const [form] = Form.useForm();
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    //Only for Admins
    if (user.groupID?.name === 'admin') { 
      // Get Group from backend
      const fetchGroups = async () => {
        try {
          const response = await axios.get('/groups');
          setGroups(response.data);
        } catch (error) {
          console.error('Error fetching groups:', error);
          message.error('Error fetching groups');
        }
      };

      // Get Clients from backend
      const fetchClients = async () => {
        try {
          const response = await axios.get('/clients');
          setClients(response.data);
        } catch (error) {
          console.error('Error fetching clients:', error);
          message.error('Error fetching clients');
        }
      };

      fetchGroups();
      fetchClients();
    }
  }, [user.groupID?.name]);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        username: user.username,
        firstname: user.firstname,
        lastname: user.lastname,
        slackID: user.slackID || '',
        preferredTheme: user.preferredTheme === 'dark', 
      });
    }
    if (user.groupID?.name === 'admin' && groups.length > 0 && clients.length > 0) {
      form.setFieldsValue({
        slackID: user.slackID || '',
        groupID: user.groupID._id || groups[1]?._id,
        clientID: user.clientID._id || clients[0]?._id,
      });
    }
  }, [user, groups, clients, form]);

  const onFinish = async (values) => {
    try {
      const updatedValues = {
        ...values,
        preferredTheme: values.preferredTheme ? 'dark' : 'light', // Convert true/false to “dark”/“light”.
      };

      if (user && user._id) {
        // Updates the existing user profile
        const response = await axios.put(`/users/${user._id}`, updatedValues);
        
        const updatedUser = response.data;
        setUser(updatedUser);

        message.success('Profile updated successfully!');
      } else {
        // Create a new user
        const response = await axios.post('/users', updatedValues);
        const updatedUser = response.data;
        setUser(updatedUser);
        message.success('User created successfully!');
      }

      setDarkTheme(values.preferredTheme === 'dark'); 
    } catch (error) {
      message.error('Error updating profile');
    }

    setIsFormChanged(false);
    navigate('/');
  };

  const onFieldsChange = () => {
    setIsFormChanged(true);
  };

  return (
    <div style={{ padding: '24px' }}>
      <h2>{user ? 'Edit Profile' : 'Add New User'}</h2>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFieldsChange={onFieldsChange}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Form.Item
              name="username"
              label="Username"
              rules={[
                { required: !user, message: 'Please input your username!' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: !user, message: 'Please input your password!' }
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Form.Item
              name="firstname"
              label="First Name"
              rules={[
                { required: !user, message: 'Please input your first name!' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <Form.Item
              name="lastname"
              label="Last Name"
              rules={[
                { required: !user, message: 'Please input your last name!' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {user.groupID?.name === 'admin' && groups.length > 0 && clients.length > 0 && (
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Form.Item
              name="groupID"
              label={
                <span>
                  <UsergroupAddOutlined style={{ fontSize: '18px', color: '#1890ff', marginRight: '5px' }} />
                  Group
                </span>
              }
              rules={[{ required: true, message: 'Please select a group!' }]}
            >
              <Select>
                {groups.map((group) => (
                  <Option key={group._id} value={group._id}>
                    {group.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Form.Item
              name="clientID"
              label={
                <span>
                  <SettingOutlined style={{ fontSize: '18px', color: '#1890ff', marginRight: '5px' }} />
                  Clients
                </span>
              }
            >
              <Select>
                {clients.map((client) => (
                  <Option key={client._id} value={client._id}>
                    {client.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Form.Item
              name="slackID"
              label={
                <span>
                  <SlackOutlined style={{ fontSize: '18px', color: '#611f69', marginRight: '5px' }} />
                  Slack ID
                </span>
              }
              rules={[{ required: false, message: 'Please input your Slack ID!' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        )}

        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12}>
            <Form.Item
              name="preferredTheme"
              label="Theme"
              valuePropName="checked"
            >
              <Switch
                checkedChildren={<MoonOutlined />}
                unCheckedChildren={<BulbOutlined />}
                style={{ transform: 'scale(1.5)', marginLeft: '10px' }}
                className="custom-inline-switch"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Button type="primary" htmlType="submit" disabled={!isFormChanged}>
            Save
          </Button>
          <Button
            style={{ marginLeft: '10px' }}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ProfilePage;
